import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Clock, User, Mail } from 'lucide-react';
import articleContent from '../data/articleContent';

const articles = Object.entries(articleContent).map(([key, a]) => ({ id: parseInt(key), ...a }));

const categories = ['All', 'Market Outlook', 'Digital Assets', 'Private Credit', 'Forex', 'Commodities', 'Environmental', 'Real Assets', 'Hedge Funds', 'Structured Products', 'Collectibles', 'Wealth Advisory'];

const Insights = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, delay }
  });

  const filtered = activeCategory === 'All' ? articles : articles.filter(a => a.category === activeCategory);
  const featured = articles.slice(0, 2);

  return (
    <div style={{ paddingTop: '5rem', color: 'var(--text-primary)' }}>
      {/* Hero */}
      <section style={{ padding: '6rem 5%', textAlign: 'center' }}>
        <motion.p {...fadeUp()} style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.9rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Insights & Research</motion.p>
        <motion.h1 {...fadeUp(0.1)} style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: '800', marginBottom: '1.5rem', maxWidth: '800px', margin: '0 auto 1.5rem' }}>
          Institutional-Grade Research & Market Intelligence
        </motion.h1>
        <motion.p {...fadeUp(0.2)} style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', maxWidth: '650px', margin: '0 auto', lineHeight: '1.7' }}>
          Weekly publications from our CIO, research team, and investment committee covering macro trends, asset class deep-dives, and actionable portfolio strategies.
        </motion.p>
      </section>

      {/* Category Filter */}
      <div style={{ padding: '0 5%', marginBottom: '3rem', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '0', overflowX: 'auto', paddingBottom: '0' }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              padding: '0.8rem 1.2rem', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', transition: 'all 0.2s',
              color: activeCategory === cat ? 'var(--accent-primary)' : 'var(--text-secondary)',
              borderBottom: activeCategory === cat ? '2px solid var(--accent-primary)' : '2px solid transparent'
            }}>{cat}</button>
          ))}
        </div>
      </div>

      {/* Featured Articles - Media Cards */}
      {activeCategory === 'All' && (
        <section style={{ padding: '0 5% 3rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
            {featured.map((a, i) => (
              <motion.div key={a.id} {...fadeUp(i * 0.1)}
                onClick={() => navigate(`/insights/${a.id}`)}
                style={{ background: 'var(--bg-secondary)', borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.3s' }}
                whileHover={{ y: -4, boxShadow: '0 12px 35px rgba(0,0,0,0.12)' }}
              >
                <div style={{ height: '220px', backgroundImage: `url(${a.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6))' }} />
                  <div style={{ position: 'absolute', top: '1rem', left: '1rem', display: 'flex', gap: '0.5rem' }}>
                    <span style={{ background: 'var(--accent-primary)', color: '#000', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '700' }}>FEATURED</span>
                    <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '600', backdropFilter: 'blur(4px)' }}>{a.category}</span>
                  </div>
                </div>
                <div style={{ padding: '1.5rem 2rem 2rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '800', marginBottom: '0.8rem', lineHeight: '1.3' }}>{a.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '0.95rem', marginBottom: '1.2rem' }}>{a.excerpt}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', color: 'var(--text-secondary)', fontSize: '0.8rem', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}><User size={13} />{a.author}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Clock size={13} />{a.readTime}</span>
                    </div>
                    <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.85rem' }}>Read →</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Article Grid - Media Cards */}
      <section style={{ padding: '0 5% 4rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {filtered.filter(a => activeCategory !== 'All' || a.id > 2).map((a, i) => (
              <motion.div key={a.id} {...fadeUp(i * 0.06)}
                onClick={() => navigate(`/insights/${a.id}`)}
                style={{
                  background: 'var(--bg-secondary)', borderRadius: '20px', overflow: 'hidden',
                  border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.3s',
                  display: 'flex', flexDirection: 'column'
                }}
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }}
              >
                {/* Media Image */}
                <div style={{ height: '180px', backgroundImage: `url(${a.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '0.8rem', left: '0.8rem' }}>
                    <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '600', backdropFilter: 'blur(4px)' }}>{a.category}</span>
                  </div>
                </div>
                {/* Text Content */}
                <div style={{ padding: '1.2rem 1.5rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '0.6rem', lineHeight: '1.3' }}>{a.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '0.88rem', marginBottom: '1rem' }}>
                      {a.excerpt.length > 120 ? a.excerpt.substring(0, 120) + '...' : a.excerpt}
                    </p>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '0.8rem' }}>
                    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: '800', color: '#000' }}>
                        {a.author.split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </div>
                      <span>{a.date} · {a.readTime}</span>
                    </div>
                    <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.85rem' }}>Read →</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)' }}>
              <p>No articles found in this category yet. Check back soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section style={{ padding: '4rem 5% 6rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <motion.div {...fadeUp()}>
            <Mail size={40} style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '0.8rem' }}>Weekly Market Intelligence</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.7' }}>
              Join 35,000+ investors receiving our CIO Weekly Brief — covering macro trends, asset class signals, and portfolio positioning across all 12 strategies.
            </p>
            <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', gap: '0.8rem', maxWidth: '450px', margin: '0 auto' }}>
              <input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)}
                style={{ flex: 1, padding: '0.9rem 1rem', borderRadius: '12px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '1rem', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
              />
              <button type="submit" style={{ background: 'var(--accent-gradient)', padding: '0.9rem 1.5rem', borderRadius: '12px', border: 'none', color: '#000', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>Subscribe</button>
            </form>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.8rem' }}>Unsubscribe anytime. No spam, ever.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
